import { ApplicationConfig, inject, provideAppInitializer, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { AuthService } from './core/auth/auth.service';
import { SessionExpiryWarningService } from './core/auth/session-expiry-warning.service';
import { SessionService } from './core/auth/session.service';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { csrfInterceptor } from './core/interceptors/csrf.interceptor';
import { refreshTokenInterceptor } from './core/interceptors/refresh-token.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { environment } from '../environments/environment';

const CSRF_COOKIE_NAME = 'badge_csrf_token';
const PUBLIC_ROUTE_PREFIXES = [
  '/login',
  '/logout',
  '/password-dimenticata',
  '/reimposta-password',
  '/registrazione'
] as const;

function hasCookie(name: string): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  const encodedName = `${encodeURIComponent(name)}=`;
  return document.cookie
    .split(';')
    .some((part) => part.trim().startsWith(encodedName));
}

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideAppInitializer(async () => {
      const session = inject(SessionService);
      const sessionExpiry = inject(SessionExpiryWarningService);
      const auth = inject(AuthService);
      const pathname = typeof window === 'undefined' ? '' : window.location.pathname;

      if (isPublicRoute(pathname)) {
        if (!environment.production) {
          console.warn('[appInitializer] public route -> skip auth bootstrap', { pathname });
        }
        session.clearCurrentUser();
        sessionExpiry.stop();
        return;
      }

      if (!hasCookie(CSRF_COOKIE_NAME)) {
        if (!environment.production) {
          console.warn('[appInitializer] missing CSRF cookie -> clear currentUser', { pathname });
        }
        session.clearCurrentUser();
        sessionExpiry.stop();
        return;
      }

      try {
        const tokens = await firstValueFrom(auth.refresh());
        session.setTokenPair(tokens);
        const me = await firstValueFrom(auth.me());
        session.setCurrentUser(me);
        sessionExpiry.start();
      } catch {
        if (!environment.production) {
          console.warn('[appInitializer] refresh/me bootstrap failed -> clear session', { pathname });
        }
        session.clearSession();
        sessionExpiry.stop();
      }
    }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor, csrfInterceptor, refreshTokenInterceptor, errorInterceptor]))
  ]
};
